/**
 * Enhanced BaseService with built-in request deduplication and performance optimizations
 */

import { BaseService } from './BaseService';
import { RequestDeduplicator } from './RequestDeduplication';
import type { ServiceConfig, ServiceResponse } from './ServiceTypes';

export interface EnhancedServiceConfig extends ServiceConfig {
  deduplication?: {
    enabled: boolean;
    ttl?: number;
  };
  smartCaching?: {
    enabled: boolean;
    sharedCache?: boolean; // Use global cache for common data
  };
}

export abstract class EnhancedBaseService extends BaseService {
  private deduplicator: RequestDeduplicator;
  private static sharedCache = new Map<string, { data: any; timestamp: number }>();

  constructor(config?: EnhancedServiceConfig) {
    super(config);

    // Initialize deduplicator
    this.deduplicator = new RequestDeduplicator({
      enabled: config?.deduplication?.enabled ?? true,
      ttl: config?.deduplication?.ttl ?? 5000,
    });
  }

  /**
   * Execute operation with deduplication
   */
  protected async executeWithDedup<T>(
    operation: () => Promise<T>,
    key?: string,
  ): Promise<ServiceResponse<T>> {
    // Generate key if not provided
    const dedupKey = key || this.generateOperationKey(operation);

    return this.execute(async () => {
      return this.deduplicator.execute(dedupKey, operation);
    });
  }

  /**
   * Get data with shared caching (for common data like user profiles)
   */
  protected async getSharedData<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = 5 * 60 * 1000, // 5 minutes
  ): Promise<T> {
    const now = Date.now();
    const cached = EnhancedBaseService.sharedCache.get(key);

    if (cached && now - cached.timestamp < ttl) {
      this.log.debug('Shared cache hit', { key });
      return cached.data as T;
    }

    // Use deduplication for the fetch operation
    const data = await this.deduplicator.execute(key, fetcher, ttl);

    // Store in shared cache
    EnhancedBaseService.sharedCache.set(key, {
      data,
      timestamp: now,
    });

    // Limit cache size
    if (EnhancedBaseService.sharedCache.size > 100) {
      const firstKey = EnhancedBaseService.sharedCache.keys().next().value;
      EnhancedBaseService.sharedCache.delete(firstKey);
    }

    return data;
  }

  /**
   * Generate a unique key for an operation
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private generateOperationKey(operation: Function): string {
    // Use function string representation as a simple key
    // In production, you might want to use a more sophisticated approach
    return `${this.constructor.name}:${operation.toString().substring(0, 100)}`;
  }

  /**
   * Get user profile with shared caching
   */
  protected async getUserProfileShared(userId: string): Promise<any> {
    return this.getSharedData(`user-profile:${userId}`, async () => {
      const { data, error } = await this.client
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      return data;
    });
  }

  /**
   * Clear shared cache (useful for testing or forced refresh)
   */
  static clearSharedCache(): void {
    EnhancedBaseService.sharedCache.clear();
  }

  /**
   * Get shared cache statistics
   */
  static getSharedCacheStats(): { size: number; keys: string[] } {
    return {
      size: EnhancedBaseService.sharedCache.size,
      keys: Array.from(EnhancedBaseService.sharedCache.keys()),
    };
  }
}

// Export a factory function for easy migration
export function enhanceService<T extends BaseService>(
  ServiceClass: new (...args: any[]) => T,
  config?: EnhancedServiceConfig,
): T {
  return new ServiceClass(config);
}
